<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxStatusBadge from 'dashboard/components/widgets/conversation/bmt/InboxStatusBadge.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const showArchived = ref(false);
const archivedInboxes = ref([]);
const isLoadingArchived = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  if (showArchived.value) return archivedInboxes.value;
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?\n\n\u26a0\ufe0f ${t('INBOX_MGMT.DELETE.CONFIRM.HISTORY_WARNING')}`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const loadArchived = async () => {
  isLoadingArchived.value = true;
  try {
    const resp = await store.dispatch('inboxes/getArchived');
    archivedInboxes.value = (resp || []).slice().sort((a, b) => a.name.localeCompare(b.name));
  } catch (e) {
    archivedInboxes.value = [];
  } finally {
    isLoadingArchived.value = false;
  }
};

const toggleArchived = () => {
  showArchived.value = !showArchived.value;
  if (showArchived.value) loadArchived();
};

const archiveInbox = async (inbox) => {
  if (!window.confirm(t('INBOX_MGMT.ARCHIVE.CONFIRM', { name: inbox.name }))) return;
  try {
    await store.dispatch('inboxes/archive', inbox.id);
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.ARCHIVE.SUCCESS'));
  } catch (e) {
    useAlert(t('INBOX_MGMT.ARCHIVE.ERROR'));
  }
};

const unarchiveInbox = async (inbox) => {
  try {
    await store.dispatch('inboxes/unarchive', inbox.id);
    await store.dispatch('inboxes/get');
    archivedInboxes.value = archivedInboxes.value.filter(i => i.id !== inbox.id);
    useAlert(t('INBOX_MGMT.UNARCHIVE.SUCCESS'));
  } catch (e) {
    useAlert(t('INBOX_MGMT.UNARCHIVE.ERROR'));
  }
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="showArchived ? $t('INBOX_MGMT.ARCHIVE.SHOW_ACTIVE') : $t('INBOX_MGMT.ARCHIVE.SHOW_ARCHIVED')"
            slate
            sm
            @click="toggleArchived"
          />
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
              </span>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
                class="text-body-main text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex gap-3 justify-end items-center">
            <InboxStatusBadge :inbox-id="inbox.id" />
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin && !showArchived"
              :label="$t('INBOX_MGMT.ARCHIVE.BUTTON_TOOLTIP')"
              slate
              sm
              class="text-xs hover:enabled:text-n-amber-11 hover:enabled:bg-n-amber-2"
              @click="archiveInbox(inbox)"
            />
            <Button
              v-if="isAdmin && showArchived"
              :label="$t('INBOX_MGMT.UNARCHIVE.BUTTON_TOOLTIP')"
              slate
              sm
              class="text-xs hover:enabled:text-n-teal-11 hover:enabled:bg-n-teal-2"
              @click="unarchiveInbox(inbox)"
            />
            <Button
              v-if="isAdmin && !showArchived"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
